import { cn, focusRing } from "@simpro/ui";
import { Check, Plus } from "lucide-react";

export interface AddServiceButtonProps {
  name: string;
  added: boolean;
  /** Five are already added: the rest wait, since the server allows no more (`Appointment.MaxLines`). */
  disabled?: boolean;
  /** The row behind it is filled (an added card), so the button takes the surface colour instead of ink. */
  onAccent?: boolean;
  size?: "md" | "lg";
  onToggle: () => void;
}

/**
 * The round + that adds a service, morphing into ✓ once added (design note salon-booking-design-2.md
 * H2; premium redesign, design note salon-booking-premium-redesign.md section 5.3 items 8 and 15). A
 * toggle button: pressed means the service is in the visit. Idle it is a hairline circle; added it fills
 * with the ink, and the + turns a quarter and fades as the ✓ arrives in its place.
 */
export function AddServiceButton({
  name,
  added,
  disabled = false,
  onAccent = false,
  size = "md",
  onToggle,
}: AddServiceButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={added}
      aria-label={`Add ${name}`}
      disabled={!added && disabled}
      onClick={onToggle}
      className={cn(
        "relative grid size-11 shrink-0 place-items-center rounded-full transition-[background-color,color,box-shadow] duration-300 ease-[cubic-bezier(0.2,0.7,0.2,1)] disabled:opacity-40",
        size === "lg" && "lg:size-12",
        added
          ? onAccent
            ? "bg-surface text-heading"
            : "bg-ink text-on-ink"
          : "bg-transparent text-heading ring-[1.5px] ring-edge ring-inset hover:ring-heading",
        focusRing,
      )}
    >
      <PlusCheck on={added} />
    </button>
  );
}

/**
 * The + that becomes a ✓ (section 5.3 items 8 and 15): both drawn on top of each other, the one not
 * shown turned a quarter and shrunk away. CSS only, so it costs no motion code and stays still for
 * anyone who has asked for less motion.
 */
export function PlusCheck({ on, className }: { on: boolean; className?: string }) {
  const glyph =
    "absolute transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.2,0.7,0.2,1)] motion-reduce:transition-none";
  return (
    <span aria-hidden="true" className={cn("relative grid size-5 place-items-center", className)}>
      <Plus
        className={cn(glyph, "size-5", on ? "scale-50 rotate-90 opacity-0" : "scale-100 rotate-0 opacity-100")}
        strokeWidth={2}
      />
      <Check
        className={cn(glyph, "size-[1.125rem]", on ? "scale-100 rotate-0 opacity-100" : "scale-50 -rotate-90 opacity-0")}
        strokeWidth={2.5}
      />
    </span>
  );
}
